import classNames from 'classnames';
import { forwardRef, useRef, type ReactNode } from 'react';
import type { GearComparison, ItemRarity } from '@sorrem/shared';
import { useDragSource, useDropTarget, useHoverPopover } from '@sorrem-client/ui/hooks';
import { ItemSlotMarks } from '../ItemSlotMarks';
import { LoadoutLabel } from '../LoadoutLabel';
import { resolveDropTargetClassName, getDropTargetProps } from './drop-target';
import styles from './ItemSlot.module.scss';

interface ItemSlotProps {
    /** 'small' for accessories/inventory, 'large' for equipment/storage. */
    size: 'small' | 'large';
    /** Accessible name; for large slots, also shown as caption. Optional for inventory use. */
    label?: string;
    /** Accessible name without a caption (e.g. a grid slot naming its item); wins over `label`. */
    ariaLabel?: string;
    /** When set, the frame is a drop target: dragging an item onto it calls this to place it there. */
    onPlace?: () => void;
    /** When set, the frame is a button: clicking it (Enter/Space) calls this, to select what it holds. Moving is by dragging or the item menu. */
    onSelect?: () => void;
    /** Makes the frame draggable (carrying what it holds); called when a drag starts from it. */
    onDragStart?: () => void;
    /** Marks the frame as a valid place for the selected item. */
    isHighlighted?: boolean;
    /** Marks the frame as holding the selected item. */
    isSelected?: boolean;
    /** What the frame holds, shown in a popover while the pointer rests on it. None when unset. */
    popoverContent?: ReactNode;
    /** Right-clicking the frame opens its item's menu beside it (#267); the browser's own menu never shows. */
    onOpenMenu?: (anchor: HTMLElement) => void;
    /** The rarity of the item it holds, shown as the tier's light and badge (#341). */
    rarity?: ItemRarity;
    /** How the item it holds compares with what is worn (#293, #341), shown as chevrons in a badge; none when about even. */
    comparison?: GearComparison;
    children?: ReactNode;
}

/** A reusable item slot for equipment frames or inventory grids. Supports optional caption for large slots. */
export const ItemSlot = forwardRef<HTMLDivElement, ItemSlotProps>(function ItemSlot({
    size,
    label,
    ariaLabel: ariaLabelOverride,
    onPlace,
    onSelect,
    onDragStart,
    isHighlighted = false,
    isSelected = false,
    popoverContent,
    onOpenMenu,
    rarity,
    comparison,
    children,
}, ref) {
    const slotClass = size === 'small' ? styles.small : styles.large;
    const ariaLabel = ariaLabelOverride ?? label ?? (size === 'large' ? 'Item slot' : 'Slot');
    // A dragged item (e.g. from the Carried panel) lands here.
    const dropTarget = useDropTarget(onPlace === undefined ? undefined : { onDrop: onPlace });
    const contentRef = useRef<HTMLDivElement>(null);
    // Only the item follows the pointer, not the frame around it.
    const dragSource = useDragSource(onDragStart === undefined ? undefined : { cursor: 'move', onStart: onDragStart, ghostOf: () => contentRef.current });
    const hoverPopover = useHoverPopover(popoverContent, ref);

    return (
        <div
            {...hoverPopover}
            className={classNames(
                styles.itemSlot,
                slotClass,
                resolveDropTargetClassName({ isTarget: onPlace !== undefined || onSelect !== undefined, isHighlighted, isSelected }),
            )}
            {...getDropTargetProps(onSelect, ariaLabel)}
            {...dropTarget}
            {...dragSource}
            onContextMenu={onOpenMenu === undefined ? undefined : (event): void => {
                event.preventDefault();
                onOpenMenu(event.currentTarget);
            }}
        >
            <ItemSlotMarks rarity={rarity} comparison={comparison} />
            <div className={styles.content} ref={contentRef}>{children}</div>
            {size === 'large' && label && <LoadoutLabel className={styles.caption}>{label}</LoadoutLabel>}
        </div>
    );
});
